import type { Accessor, Resource } from "solid-js"
import { Match, Show, Switch } from "solid-js"
import { Icon } from "@/designSystem/components/icon"
import { TextField } from "@/designSystem/components/text-field"
import { useI18nContext } from "@/i18n/i18n-solid"
import type { NameStatus } from "@/lib/create-name"
import {
  affixClass,
  fadeInClass,
  inputClass,
  wrapperClass,
} from "./name-input-field.css"

interface NameInputFieldProps {
  userName: Accessor<string>
  setUserName: (value: string) => void
  nameStatus: Resource<NameStatus>
  nameDirty: Accessor<boolean>
  validName: Accessor<boolean>
  isFormValid: Accessor<boolean>
  disabled?: boolean
}

export function NameInputField(props: NameInputFieldProps) {
  const { LL } = useI18nContext()

  return (
    <TextField
      value={props.userName()}
      onChange={props.setUserName}
      validationState={props.isFormValid() ? "valid" : "invalid"}
    >
      <div class={wrapperClass}>
        <div class={affixClass}>https://</div>
        <TextField.Input
          class={inputClass}
          placeholder={LL().onboarding.yourBlogNamePlaceholder()}
          disabled={props.disabled}
        />
        <div class={affixClass}>.fika.bar</div>
        <Show when={props.nameDirty()}>
          <div class={fadeInClass}>
            <Switch>
              <Match
                when={
                  props.nameStatus() === "invalid" ||
                  props.nameStatus() === "unavailable" ||
                  props.nameStatus() === "naughty" ||
                  !props.validName()
                }
              >
                <Icon name="x" size="24" color="rose600" />
              </Match>
              <Match when={props.nameStatus() === "available"}>
                <Icon name="check" size="24" color="emerald600" />
              </Match>
              <Match when={props.nameStatus.loading}>
                <Icon name="loader" size="24" color="sand400" />
              </Match>
            </Switch>
          </div>
        </Show>
      </div>
      <div class={fadeInClass}>
        <Show when={props.nameDirty()}>
          <Switch>
            <Match when={!props.validName()}>
              <TextField.ErrorMessage>
                {LL().blog.atLeast3Characters()}
              </TextField.ErrorMessage>
            </Match>
            <Match when={props.nameStatus.loading}>
              <TextField.Description>
                {LL().blog.checkingNameAvailability()}
              </TextField.Description>
            </Match>
            <Match when={props.nameStatus() === "available"}>
              <TextField.Description>
                {LL().blog.nameAvailable()}
              </TextField.Description>
            </Match>
            <Match when={props.nameStatus() === "invalid"}>
              <TextField.ErrorMessage>
                {LL().blog.nameInvalid()}
              </TextField.ErrorMessage>
            </Match>
            <Match when={props.nameStatus() === "unavailable"}>
              <TextField.ErrorMessage>
                {LL().blog.nameUnavailable()}
              </TextField.ErrorMessage>
            </Match>
            <Match when={props.nameStatus() === "naughty"}>
              <TextField.ErrorMessage>
                {LL().blog.nameNaughty()}
              </TextField.ErrorMessage>
            </Match>
          </Switch>
        </Show>
      </div>
    </TextField>
  )
}
